// @ts-check
import { IS_DEVELOPMENT } from '@/js/common/constants';
import { f7 } from 'framework7-vue';
import { ref } from 'vue';

// These CSS files are defined in `webpack.config.cjs`
const NEW_STYLES_CSS_FILE = 'new-styles.css';
const OLD_STYLES_CSS_FILE = 'old-styles.css';
const OLD_STYLES_CLASSES_CSS_FILE = 'old-styles-classes.css';

/**
 * @typedef {object} ExtractedStyles
 * @property {string[]} new
 * @property {string[]} old
 * @property {string[]} oldClasses
 */

/** @type {import('vue').Ref<ExtractedStyles>} */
const extractedStyles = ref({
  new: [],
  old: [],
  oldClasses: [],
});

export const useNewStyles = () => {
  function initNewStylesHandling() {
    if (IS_DEVELOPMENT) {
      extractStyles();
    }

    const { currentRoute } = f7.views.current.router;

    // @ts-expect-error skip "Property 'meta' does not exist on type 'RouteParameters'."
    const shouldApplyNewStyles = !!currentRoute.route.meta?.useNewStyles;
    modifyStylesOrLinks(shouldApplyNewStyles);
    modifyStylesOrLinksOldClasses(shouldApplyNewStyles);

    f7.on('routeChange', handleRouteChange);
    f7.on('routeChanged', handleRouteChanged);
  }

  /**
   * Modifies styles or links based on environment and metadata.
   * @param {boolean} canUseNewStyles - `true` to handle new styles, `false` to handle old styles.
   * @returns {void}
   */
  function modifyStylesOrLinks(canUseNewStyles) {
    if (IS_DEVELOPMENT) {
      modifyStyles(canUseNewStyles);
    } else {
      modifyLinks(canUseNewStyles);
    }
  }

  function extractStyles() {
    /** @type {ExtractedStyles} */
    const stylesValue = { new: [], old: [], oldClasses: [] };

    const headStyles = document.querySelectorAll('style');

    headStyles.forEach((styleElement) => {
      const styleContent = styleElement.textContent ?? '';
      const beginning = styleContent.substring(0, 50);

      if (beginning.includes('/** NEW STYLE. */')) {
        stylesValue.new.push(styleContent);
      } else if (beginning.includes('/** OLD STYLE. */')) {
        stylesValue.old.push(styleContent);
      } else if (beginning.includes('/** OLD STYLE CLASSES. */')) {
        stylesValue.oldClasses.push(styleContent);
      }
    });

    extractedStyles.value = stylesValue;
  }

  /**
   * Handles route changes.
   * @param {import('framework7/types').Router.Route} newRoute - The new route.
   * @param {import('framework7/types').Router.Route} currentRoute - The currentRoute route.
   */
  function handleRouteChange(newRoute, currentRoute) {
    // This handles the case when the user is being navigated to a previous route
    // with force + back used at the same time, like in expeditions or sales orders processes
    if (Object.keys(currentRoute).length === 0) {
      return;
    }

    // @ts-expect-error skip "Property 'meta' does not exist on type 'RouteParameters'."
    const shouldApplyNewStylesInNewRoute = !!newRoute.route.meta?.useNewStyles;
    // @ts-expect-error skip "Property 'meta' does not exist on type 'RouteParameters'."
    const shouldApplyNewStylesInCurrentRoute = !!currentRoute.route?.meta?.useNewStyles;

    if (shouldApplyNewStylesInNewRoute !== shouldApplyNewStylesInCurrentRoute) {
      modifyStylesOrLinks(shouldApplyNewStylesInNewRoute);
    }
  }

  /**
   * Modifies styles or links based on environment and metadata.
   * @param {boolean} canUseNewStyles - `true` to handle new styles, `false` to handle old styles.
   * @returns {void}
   */
  function modifyStylesOrLinksOldClasses(canUseNewStyles) {
    if (!canUseNewStyles) {
      return;
    }

    if (IS_DEVELOPMENT) {
      for (const styleContent of extractedStyles.value.oldClasses) {
        const allStyles = document.querySelectorAll('style');

        for (const styleFromAllStyles of allStyles) {
          if (styleFromAllStyles.innerHTML.trim() === styleContent.trim()) {
            styleFromAllStyles.parentNode?.removeChild(styleFromAllStyles);
          }
        }
      }
    } else {
      const styleLinks = document.querySelectorAll('link[rel="stylesheet"]');
      for (const link of styleLinks) {
        const htmlLinkElement = /** @type {HTMLLinkElement} */ (link);
        const isOldStylesClasses = htmlLinkElement.href.includes(OLD_STYLES_CLASSES_CSS_FILE);

        if (isOldStylesClasses) {
          htmlLinkElement.disabled = true;
        }
      }
    }
  }

  /**
   * Handles route changes.
   * @param {import('framework7/types').Router.Route} newRoute - The new route.
   */
  function handleRouteChanged(newRoute) {
    // @ts-expect-error skip "Property 'meta' does not exist on type 'RouteParameters'."
    const shouldApplyNewStyles = !!newRoute.route.meta?.useNewStyles;
    modifyStylesOrLinksOldClasses(shouldApplyNewStyles);
  }

  /**
   * Removes or adds styles based on environment.
   * @param {boolean} canUseNewStyles - `true` to use new styles, `false` otherwise.
   * @returns {void}
   */
  function modifyStyles(canUseNewStyles) {
    const stylesToRemove = canUseNewStyles ? extractedStyles.value.old : extractedStyles.value.new;
    const stylesToAdd = canUseNewStyles ? extractedStyles.value.new : extractedStyles.value.old;

    if (!canUseNewStyles) {
      stylesToAdd.push(...extractedStyles.value.oldClasses);
    }

    for (const style of stylesToRemove) {
      removeStyle(style);
    }

    for (const style of stylesToAdd) {
      addStyle(style);
    }
  }

  /**
   * Removes a `<style>` element.
   * @param {string} styleContent - The style content to remove.
   * @returns {void}
   */
  function addStyle(styleContent) {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = styleContent;
    document.head.appendChild(styleElement);
  }

  /**
   * Removes a `<style>` element.
   * @param {string} styleContent - The style content to remove.
   * @returns {void}
   */
  function removeStyle(styleContent) {
    const styles = document.querySelectorAll('style');

    for (const style of styles) {
      if (style.innerHTML.trim() === styleContent.trim()) {
        style.parentNode?.removeChild(style);
      }
    }
  }

  /**
   * Removes or adds link elements based on environment.
   * @param {boolean} canUseNewStyles - `true` to use new styles, `false` otherwise.
   * @returns {void}
   */
  function modifyLinks(canUseNewStyles) {
    const styleLinks = document.querySelectorAll('link[rel="stylesheet"]');

    for (const link of styleLinks) {
      const htmlLinkElement = /** @type {HTMLLinkElement} */ (link);
      const isOldStyles = htmlLinkElement.href.includes(OLD_STYLES_CSS_FILE);
      const isOldStylesClasses = htmlLinkElement.href.includes(OLD_STYLES_CLASSES_CSS_FILE);
      const isNewStyles = htmlLinkElement.href.includes(NEW_STYLES_CSS_FILE);

      if ((canUseNewStyles && isOldStyles) || (!canUseNewStyles && isNewStyles)) {
        htmlLinkElement.disabled = true;
      } else if ((canUseNewStyles && isNewStyles) || (!canUseNewStyles && (isOldStyles || isOldStylesClasses))) {
        htmlLinkElement.disabled = false;
      }
    }
  }

  return {
    initNewStylesHandling,
  };
};
